import React, {createContext, useContext, useState, useEffect} from 'react';
import './App.scss';
import {
    Layout,
    Button,
    Spin,
    Dropdown,
    ConfigProvider,
    theme,
    Space,
    Row,
} from 'antd';
import Icon, {
    UserOutlined,
    LoadingOutlined,
    MoonOutlined,
} from '@ant-design/icons';
import AdminNavMenu from "./components/admin-nav-menu";
import {Routes, Route, Link} from 'react-router-dom';
import Comments from "./pages/comments";
import Dashboard from "./pages/dashboard";
import Translations from "./pages/translations";
import TranslationView from "./pages/translation-view";
import Api from "./api.js";
import Recipes from "./pages/recipes";
import Barcode from "./pages/barcode";
import RecipeView from "./pages/recipe-view";
import User from "./pages/user.js";
import Users from "./pages/users.js";
import {getCookie, redirect, removeCookie, setCookie} from "./helpers";
import Settings from "./pages/settings.js";
import Ingredients from "./pages/ingredients";
import IngredientView from "./pages/ingredient-view";
import Selver from "./pages/selver";
import Levels from "./pages/levels";
import Actions from "./pages/actions";
import AdventCalendar from "./pages/advent-calendar";
import Tasks from "./pages/tasks";
import ShortUrls from "./pages/short-urls";
import AdventView from "./pages/advent-view";
import TaskView from "./pages/task-view";
import DefaultSurveyQuestions from "./pages/default-survey-questions";
import Reports from "./pages/reports";
import TrainingVideosView from "./pages/training-videos-view";
import FreewareIngredientsView from "./pages/freeware-ingredients-view";
import SubstituteGroupsView from "./pages/substitute-groups-view";
import SubstituteGroupView from "./pages/substitute-group-view";
import AllergensView from "./pages/ingredients/allergens-view";
import AllergenView from "./pages/ingredients/allergen-view";
import CustomUnitsView from "./pages/ingredients/custom-units-view";
import CustomUnitView from "./pages/ingredients/custom-unit-view";
import MenuTemplatesView from "./pages/menu-templates-view";
import MenuTemplateView from "./pages/menu-template-view";
import TrainingVideosCategoryView from "./pages/training-videos-category-view";
import NotificationsView from "./pages/notifications-view";
import NotificationView from "./pages/notification-view";
import TrainingVideoView from "./pages/training-video-view";
import BingoChallengesView from "./pages/bingo-challenges-view";
import Bingos from "./pages/bingos";
import BingoChallengeCategoryView from "./pages/bingo-challenge-category-view";
import BingoChallengeView from "./pages/bingo-challenge-view";
import Exercises from "./pages/exercises";
import {HappyProvider} from '@ant-design/happy-work-theme';
import ScrollToTop from "./ScrollToTop";
import Login from "./pages/login";
import Invoices from "./pages/invoices";
import ErrorLog from "./pages/error-log";
import Wellness from "./pages/wellness";
import Competitions from "./pages/competitions.js";
import AppVisitedReport from "./pages/app-visited-report.js";
import ActiveInactiveReport from "./pages/active-inactive-report.js";
import CompetitionView from "./pages/competition-view";
import CoursesView from "./pages/courses-view";
import BadgesView from "./pages/badges-view";
import ReceiptsView from "./pages/receipts-view";
import TagsView from "./pages/tags-view";
import BannersView from "./pages/banners-view";
import ContentCategories from "./pages/content-categories";
import RecipeCategoryMessages from "./pages/recipe-category-messages";
import AdminLogs from "./pages/admin-logs";
import AdminStartGuides from "./pages/start-guides";
import FoodScans from "./pages/food-scans";
import GenerateBlogPost from "./pages/generate-blog-post";
import CancelRetentionDiscounts from "./pages/cancel-retention-discounts";
import MenuLinksView from "./pages/menu-links-view";
import EventAnalytics from "./pages/event-analytics";
// import ContentVideos from "./pages/content-videos";
// import ContentFiles from "./pages/content-files";

const {Header, Footer, Sider} = Layout;

const ThemeContext = createContext();
export const useTheme = () => useContext(ThemeContext);

const App = ({}) => {
    const [collapsed, setCollapsed] = useState('');
    const [hasAccess, setHasAccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(!getCookie('white_mode'));

    const toggleTheme = () => {
        if (isDarkMode) {
            setCookie('white_mode', true, 365);
        } else {
            removeCookie('white_mode');
        }

        setIsDarkMode(!isDarkMode);
    };

    useEffect(() => {
        Api.auth().then(res => {
            setUser(res.data.data);

            setTimeout(() => {setLoading(false)}, 1500)

            if (process.env.REACT_APP_WHITELIST.includes(res.data.data.email)) {
                setHasAccess(true);
            }
        }).catch(err => {
            setTimeout(() => {setLoading(false)}, 1500)
        })
    }, [])

    const onCollapse = collapsed => {
        setCollapsed(collapsed);
    };

    if (loading) {
        return (
            <div>
                <Layout style={{minHeight: '100vh', backgroundColor: '#141414', textAlign: "center"}}>
                    <Row style={{height: "100vh"}} type="flex" justify="center" align="middle">
                        <Space direction="vertical">
                            <img src="/logo-white.png" alt="" style={{width: 120}} className="logo-sm"/>
                            <Spin className="loading" indicator={<LoadingOutlined style={{fontSize: 24}} spin/>}/>
                        </Space>
                    </Row>
                </Layout>
            </div>
        )
    }

    if (!hasAccess) {
        return (
            <ThemeContext.Provider value={{isDarkMode, toggleTheme}}>
                <ConfigProvider
                    locale={'en'}
                    theme={{
                        algorithm: theme.darkAlgorithm,
                        token: {
                            fontSize: 14,
                            colorBgLayout: "white",
                            fontFamily: "Nunito, sans-serif",
                            borderRadiusLG: 12,
                            fontSizeLG: 14,
                            boxShadowTertiary: "0 4px 15px #eaeff1",
                            colorBgContainer: "#141414",
                        },
                        components: {
                            Layout: {
                                triggerBg: "#141414"
                            },
                            Header: {
                                triggerBg: "#141414"
                            },
                        },
                    }}
                >
                    <Layout style={{minHeight: '100vh', backgroundColor: '#141414'}}>
                        <Routes>
                            <Route path="*" element={<Login user={user} setUser={setUser} setHasAccess={setHasAccess}/>}/>
                        </Routes>
                    </Layout>
                </ConfigProvider>
            </ThemeContext.Provider>
        )
    }

    const logout = () => {
        removeCookie('fl_admin_access_token');
        removeCookie('fl_admin_refresh_token');
        redirect("https://admin.fitlap.ee")
    }

    const userMenuItems = [
        {
            key: 'fitlap',
            label: <a href="https://app.fitlap.ee">Fitlap</a>,
        },
        {
            key: 'settings',
            label: <Link to={"/settings"}>Settings</Link>,
        },
        {
            key: 'logout',
            label: 'Log out',
        },
    ];

    const changeSite = (value) => {
        setCookie('api_url', value, 365);
        window.location.reload();
    }

    return (
        <ThemeContext.Provider value={{isDarkMode, toggleTheme}}>
            <ConfigProvider
                locale={'en'}
                theme={{
                    algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
                    token: {
                        fontSize: 14,
                        colorBgLayout: "white",
                        fontFamily: "Nunito, sans-serif",
                        borderRadiusLG: 12,
                        fontSizeLG: 14,
                        boxShadowTertiary: isDarkMode
                            ? "0 6px 16px rgba(0, 0, 0, 0.45)"
                            : "0 4px 15px #eaeff1",
                        colorBgContainer: isDarkMode ? "#141414" : "white",
                    },
                    components: {
                        Layout: {
                            triggerBg: isDarkMode ? "#141414" : "white"
                        },
                        Header: {
                            triggerBg: isDarkMode ? "#141414" : "white"
                        },
                    },
                }}
            >
                <Header style={{backgroundColor: '#141414'}} className="header">
                    <a href="/">
                        <img src="/logo-white.png" alt="" className="logo-sm"/>
                    </a>

                    <div className="user-nav">
                        <Space>
                            <HappyProvider>
                            <Button onClick={toggleTheme} size="large" style={{borderRadius: 100, width: 40}}><Icon
                                component={MoonOutlined}/></Button>
                                </HappyProvider>
                            <Dropdown
                                menu={{
                                    items: userMenuItems,
                                    onClick: ({ key }) => {
                                        if (key === 'logout') logout();
                                    },
                                }}
                            >
                                <Button size="large" style={{borderRadius: 100, width: 40}}><Icon
                                    component={UserOutlined}/></Button>
                            </Dropdown>
                        </Space>
                    </div>
                </Header>

                <HappyProvider>
                    <Layout className={isDarkMode ? "darkmode" : "lightmode"} style={{minHeight: '100vh', backgroundColor: isDarkMode ? '#141414' : '#ffffff'}}>
                        <Sider
                            width={220}
                            style={{  background: isDarkMode ? '#141414' : "#ffffff" }}
                            collapsible theme={isDarkMode ? "dark" : "light"} collapsed={collapsed}
                            onCollapse={onCollapse}>
                            <AdminNavMenu onSiteChange={changeSite} />
                        </Sider>


                        <ScrollToTop>
                            <Routes>
                                <Route path="/" element={<Dashboard user={user}/>}/>
                                <Route path="/comments" element={<Comments/>}/>
                                <Route exact path="/translations" element={<Translations/>}/>
                                <Route exact path="/translations/:id" element={<TranslationView/>}/>
                                <Route exact path="/recipes" element={<Recipes/>}/>
                                <Route exact path="/recipe/:id" element={<RecipeView key={'edit'}/>}/>
                                <Route exact path="/create-recipe" element={<RecipeView key={'create'}/>}/>
                                <Route exact path="/users" element={<Users/>}/>
                                <Route exact path="/user/:id" element={<User admin={user}/>}/>
                                <Route exact path="/content/categories" element={<ContentCategories/>}/>
                                <Route exact path="/recipe-messages" element={<RecipeCategoryMessages/>}/>
                                {/*<Route exact path="/content/videos" element={<ContentVideos/>}/>*/}
                                {/*<Route exact path="/content/files" element={<ContentFiles/>}/>*/}
                                <Route exact path="/barcode" element={<Barcode/>}/>
                                <Route exact path="/levels" element={<Levels/>}/>
                                <Route exact path="/actions" element={<Actions/>}/>
                                <Route exact path="/ingredients" element={<Ingredients/>}/>
                                <Route exact path="/ingredients/freeware" element={<FreewareIngredientsView/>}/>
                                <Route exact path="/ingredients/substitute-groups" element={<SubstituteGroupsView/>}/>
                                <Route exact path="/ingredients/substitute-groups/:id" element={<SubstituteGroupView/>}/>
                                <Route exact path="/ingredients/allergens" element={<AllergensView/>}/>
                                <Route exact path="/ingredients/allergens/:id" element={<AllergenView/>}/>
                                <Route exact path="/ingredients/custom-units" element={<CustomUnitsView/>}/>
                                <Route exact path="/ingredients/custom-units/:id" element={<CustomUnitView/>}/>
                                <Route exact path="/ingredient/:id" element={<IngredientView/>}/>
                                <Route exact path="/ingredient/:id" element={<IngredientView/>}/>
                                <Route exact path="/selver" element={<Selver/>}/>
                                <Route exact path="/tasks" element={<Tasks/>}/>
                                <Route exact path="/task/:id" element={<TaskView/>}/>
                                <Route exact path="/settings" element={<Settings user={user}/>}/>
                                <Route exact path="/short-url" element={<ShortUrls/>}/>
                                <Route exact path="/advent-calendar" element={<AdventCalendar/>}/>
                                <Route exact path="/advent-calendar/:id" element={<AdventView/>}/>
                                <Route exact path="/survey/questions" element={<DefaultSurveyQuestions/>}/>
                                <Route exact path="/reports" element={<Reports/>}/>
                                <Route exact path="/food-scans" element={<FoodScans/>}/>
                                <Route path="/event-analytics" element={<EventAnalytics/>}/>
                                <Route exact path="/generate-blog" element={<GenerateBlogPost/>}/>
                                <Route exact path="/bingos" element={<Bingos/>}/>
                                <Route exact path="/bingos/:id" element={<BingoChallengesView/>}/>
                                <Route exact path="/bingo/challenges/:id" element={<BingoChallengeView/>}/>
                                <Route exact path="/bingo/categories/:id" element={<BingoChallengeCategoryView/>}/>
                                <Route exact path="/training-videos" element={<TrainingVideosView/>}/>
                                <Route exact path="/training-videos/:id" element={<TrainingVideoView/>}/>
                                <Route exact path="/training-videos/categories/:id"
                                       element={<TrainingVideosCategoryView/>}/>
                                <Route exact path="/menu-templates" element={<MenuTemplatesView/>}/>
                                <Route exact path="/menu-templates/:id" element={<MenuTemplateView/>}/>
                                {/*Notifications*/}
                                <Route exact path="/notifications" element={<NotificationsView/>}/>
                                <Route exact path="/notifications/create" element={<NotificationView/>}/>
                                <Route exact path="/notifications/:id" element={<NotificationView/>}/>

                                <Route exact path="/invoices" element={<Invoices/>}/>
                                <Route exact path="/cancel-retention-discounts" element={<CancelRetentionDiscounts/>}/>
                                <Route exact path="/wellness" element={<Wellness/>}/>
                                <Route exact path="/competitions" element={<Competitions/>}/>
                                <Route exact path="/competitions/:id" element={<CompetitionView/>}/>
                                <Route exact path="/app-visited" element={<AppVisitedReport/>}/>
                                <Route exact path="/active-inactive" element={<ActiveInactiveReport/>}/>

                                <Route path="/exercises" element={<Exercises/>}/>
                                <Route path="/courses" element={<CoursesView/>}/>
                                <Route path="/error-log" element={<ErrorLog/>}/>
                                <Route path="/badges" element={<BadgesView/>}/>
                                <Route path="/receipts" element={<ReceiptsView/>}/>
                                <Route path="/tags" element={<TagsView/>}/>
                                <Route path="/banners" element={<BannersView/>}/>
                                <Route path="/menu-links" element={<MenuLinksView/>}/>
                                <Route path="/admin-logs" element={<AdminLogs/>}/>
                                <Route path="/start-guides" element={<AdminStartGuides/>}/>
                            </Routes>
                        </ScrollToTop>
                    </Layout>
                </HappyProvider>

                <Footer style={{
                    textAlign: 'center',
                    backgroundColor: isDarkMode ? '#141414' : '#f0f2f5',
                    color: isDarkMode ? '#bfbfbf' : '#595959'
                }}>
                    ©2024 Created by Fitlap
                </Footer>
            </ConfigProvider>
        </ThemeContext.Provider>
    );
}

export default App;

